import { flexRender } from '@tanstack/react-table';
import { SourcingFunnelDetails } from 'api-clients/monolith/models/SourcingFunnelDetails';
import clsx from 'clsx';
import _range from 'lodash/range';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useDataTable } from '@/hooks/use-data-table';

import { useSourcingOpeningsColumns } from './columns';
import { SourcingOpenings } from './sourcing-openings';

interface AggregateViewProps {
  aggregateData: SourcingFunnelDetails[];
  sourcingOpenings: SourcingFunnelDetails[];
  isFetching: boolean;
}

export function AggregateView({
  aggregateData,
  sourcingOpenings,
  isFetching,
}: AggregateViewProps) {
  const intl = useIntl();
  const columns = useSourcingOpeningsColumns();
  const gridTemplateColumns = `auto repeat(${columns.length - 1}, 1fr)`;

  const [isExpanded, setIsExpanded] = useState(false);

  const { table: aggregateDataTable } = useDataTable<SourcingFunnelDetails>({
    columns,
    data: aggregateData,
    rowCount: 1,
  });

  const { table: sourcingOpeningsTable } = useDataTable<SourcingFunnelDetails>({
    columns,
    data: sourcingOpenings,
    rowCount: sourcingOpenings.length,
  });

  if (isFetching) return <AggregateView.Skeleton />;

  return (
    <>
      {aggregateDataTable.getRowModel().rows.map(row => (
        <div
          className={`hover:bg-fx-neutral-50 border-fx-neutral-200 flex flex-col justify-center rounded-lg border bg-white shadow-sm
    transition-colors`}
          key={row.id}
        >
          <div className="grid items-start" style={{ gridTemplateColumns }}>
            {row.getVisibleCells().map(cell => (
              <div
                key={cell.id}
                className={clsx(
                  'flex flex-col items-start gap-2 overflow-hidden py-6 px-5',
                  cell.column.id === 'name' && 'max-w-[240px]',
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}

                {/* Show button to expand reach channels only when the channel is Reach and column is the channel name */}
                {cell.column.id === 'name' && (
                  <Button
                    variant="link"
                    size="sm"
                    className="hover:no-underline"
                    onClick={() => setIsExpanded(!isExpanded)}
                  >
                    {intl.formatMessage(
                      {
                        defaultMessage: '{count} Openings',
                        description:
                          'Label showing the number of job openings in this group where {count} is the number of openings',
                      },
                      { count: sourcingOpenings.length },
                    )}
                    {isExpanded ? <ChevronUp /> : <ChevronDown />}
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Show sourcing openings when the aggregate data is expanded */}
          {isExpanded && (
            <>
              <span className="bg-fx-neutral-100 mx-5 h-px w-auto" />
              <SourcingOpenings
                table={sourcingOpeningsTable}
                gridTemplateColumns={gridTemplateColumns}
              />
            </>
          )}
        </div>
      ))}
    </>
  );
}

AggregateView.Skeleton = function AggregateViewSkeleton() {
  return (
    <div
      className={`hover:bg-fx-neutral-50 border-fx-neutral-200 flex flex-col justify-center rounded-lg border bg-white shadow-sm
    transition-colors`}
    >
      <div
        className="grid items-center"
        style={{ gridTemplateColumns: 'auto repeat(6, 1fr)' }}
      >
        <div className="h-25 flex flex-col items-start gap-2 py-6 px-5">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6" />
            <Skeleton className="h-5 w-32" />
          </div>
          <Skeleton className="w-25 h-5" />
        </div>
        {_range(6).map(colIndex => (
          <div
            key={colIndex}
            className="flex h-24 flex-col items-start gap-3 py-6 px-5"
          >
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-5 w-20" />
          </div>
        ))}
      </div>
    </div>
  );
};
